"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { History, X } from "lucide-react";
import { Button } from "@/components/shadcn/button";
import { useQuery } from "@tanstack/react-query";
import { ConversationsService } from "@/generated";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { formatDistanceToNow } from "date-fns";
import { NoxusEmptyBlock } from "@/components/UI/NoxusEmptyBlock";
import { Skeleton } from "@/components/shadcn/skeleton";
import { parseUtcDate } from "@/utils/generic";

// How long ago a chat started, from the backend's naive UTC timestamp.
export function chatAge(createdAt: string): string {
  return formatDistanceToNow(parseUtcDate(createdAt), { addSuffix: true });
}

const SKELETON_ROW_COUNT = 12;

export function GenieChatHistory({
  groupId,
  assistantId,
  panelOpen,
  visible,
  onSelect,
  onClose,
}: {
  groupId: string;
  assistantId: string;
  panelOpen: boolean;
  visible: boolean;
  onSelect: (conversationId: string) => void;
  onClose: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: [DATA_KEYS.CONVERSATIONS, groupId, "genie", assistantId],
    queryFn: () =>
      ConversationsService.listConversations({
        path: { group_id: groupId },
        query: { assistant_id: assistantId },
      }),
    enabled: panelOpen && visible && !!assistantId,
  });

  // Keep the panel laid out while visible or animating out, then drop it from
  // layout entirely. Translating off-screen still leaves a bounding box that
  // can overlap the viewport (and intercept hit-testing); display:none does not.
  const [rendered, setRendered] = useState(visible);
  useEffect(() => {
    if (visible) setRendered(true);
  }, [visible]);

  return (
    <motion.div
      initial={false}
      animate={{ x: visible ? 0 : "100%", opacity: visible ? 1 : 0 }}
      transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
      onAnimationComplete={() => {
        if (!visible) setRendered(false);
      }}
      className="absolute left-3 right-3 top-0 bottom-2.5 z-10 flex flex-col bg-white rounded-xl overflow-hidden"
      style={{
        boxShadow: "0 0 0 1px #EBEDF0, 0 3px 5px -2px rgba(0,0,0,0.22)",
        pointerEvents: visible ? "auto" : "none",
        display: rendered ? undefined : "none",
      }}
    >
      <div className="flex items-center justify-between px-4 py-3 shrink-0">
        <span className="font-semibold text-sm">Chat history</span>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="h-7 w-7"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto px-2 pb-2">
        {isLoading ? (
          Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
            <div key={i} className="px-3 py-2.5">
              <div className="flex h-5 items-center">
                <Skeleton className="h-3.5 w-2/3" />
              </div>
              <div className="flex h-4 items-center">
                <Skeleton className="h-2.5 w-24" />
              </div>
            </div>
          ))
        ) : data?.data?.items?.length ? (
          data.data.items.map((conv) => (
            <button
              key={conv.id}
              onClick={() => onSelect(conv.id)}
              className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-accent/50 transition-colors cursor-pointer"
            >
              <div className="font-medium text-sm truncate">
                {conv.name || "Untitled"}
              </div>
              <div className="text-xs text-muted-foreground">
                {chatAge(conv.created_at)}
              </div>
            </button>
          ))
        ) : (
          <NoxusEmptyBlock
            title="No chats yet"
            description="Your Genie conversations will appear here."
            icon={History}
            size="sm"
          />
        )}
      </div>
    </motion.div>
  );
}
